<script setup lang="ts">
	import { MzButton, MzIcon, MzTitle, type MzIconProps } from '@monizze/monizze-components';
	import { PhArrowRight } from '@phosphor-icons/vue';

	const props = withDefaults(
		defineProps<{
			icon?: MzIconProps['icon'];
			image?: string;
			initials?: string;
			alt?: string;
			title: string;
			description?: string;
			buttonLabel?: string;
			variant?: 'primary' | 'outline' | 'minimal';
			disabled?: boolean;
			loading?: boolean;
			href?: string;
		}>(),
		{
			icon: undefined,
			image: undefined,
			initials: undefined,
			alt: '',
			description: '',
			buttonLabel: undefined,
			variant: 'primary',
			disabled: false,
			loading: false,
			href: undefined,
		},
	);

	defineEmits<{
		action: [];
	}>();

	const hasLeading = () => Boolean(props.initials) || Boolean(props.image) || Boolean(props.icon);
</script>

<template>
	<component
		:is="href ? 'a' : 'div'"
		:href="href"
		:class="['flex w-full py-4', buttonLabel ? 'flex-col gap-4 md:flex-row md:gap-6 md:items-center' : 'flex-row items-center gap-6']"
	>
		<div class="flex flex-row items-center gap-6 grow min-w-0">
			<div v-if="hasLeading()" class="size-16 shrink-0 flex items-center justify-center">
				<span
					v-if="initials"
					class="size-full flex items-center justify-center rounded-sm bg-bg-default-secondary border border-border-default text-text-title font-bold text-xl"
				>
					{{ initials }}
				</span>
				<img v-else-if="image" :src="image" :alt="alt" class="size-full object-contain" />
				<MzIcon v-else-if="icon" :icon="icon" size="md" class="text-text-action" />
			</div>

			<div class="flex flex-col gap-2 grow min-w-0">
				<mz-title type="h4" :text="title" no-margin />
				<p v-if="description" class="text-text">{{ description }}</p>
			</div>
		</div>

		<mz-button
			:variant="variant"
			:label="buttonLabel"
			:icon="buttonLabel ? undefined : PhArrowRight"
			:disabled="disabled"
			:loading="loading"
			width="auto"
			:class="['shrink-0', buttonLabel && 'w-full md:w-auto']"
			@click="$emit('action')"
		/>
	</component>
</template>
